import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

const CoreAndGas = () => {
  const coreRef = useRef<THREE.Mesh>(null);
  const atmosphereRef = useRef<THREE.Mesh>(null);
  const particlesRef = useRef<THREE.Points>(null);
  
  const [phase, setPhase] = useState<'sweeping' | 'formed'>('sweeping');

  const count = 500;
  const initialData = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    for(let i=0; i<count; i++) {
      const r = 5 + Math.random() * 10;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      pos[i*3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i*3+1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i*3+2] = r * Math.cos(phi);
      
      // Color: blue or orange
      const isBlue = Math.random() > 0.3; // more hydrogen
      const color = isBlue ? new THREE.Color('#3B82F6') : new THREE.Color('#F97316');
      colors[i*3] = color.r;
      colors[i*3+1] = color.g;
      colors[i*3+2] = color.b;
    }
    return { pos, colors };
  }, []);

  const particlesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(initialData.pos), 3));
    geo.setAttribute('color', new THREE.BufferAttribute(initialData.colors, 3));
    return geo;
  }, [initialData]);

  useFrame((state) => {
    const time = state.clock.getElapsedTime() % 6; // 6 second loop
    
    // 0 to 4s: sweeping
    // 4 to 5s: formed
    // 5 to 6s: reset
    if (time < 4) {
      setPhase('sweeping');
      // Particles spiral in
      if (particlesRef.current) {
        const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
        for(let i=0; i<count; i++) {
          let x = positions[i*3];
          let y = positions[i*3+1];
          let z = positions[i*3+2];
          
          const r = Math.sqrt(x*x + y*y + z*z);
          if (r > 1.5) {
            // Move inward
            const inwardSpeed = 0.05 + Math.random() * 0.05;
            x -= (x / r) * inwardSpeed;
            y -= (y / r) * inwardSpeed;
            z -= (z / r) * inwardSpeed;
            
            // Spiral (rotate around Y axis slightly)
            const angle = 0.02;
            const newX = x * Math.cos(angle) - z * Math.sin(angle);
            const newZ = x * Math.sin(angle) + z * Math.cos(angle);
            
            positions[i*3] = newX;
            positions[i*3+1] = y;
            positions[i*3+2] = newZ;
          }
        }
        particlesRef.current.geometry.attributes.position.needsUpdate = true;
      }
      
      // Core grows slightly
      if (coreRef.current) {
        const scale = 1 + (time / 4) * 0.2;
        coreRef.current.scale.set(scale, scale, scale);
      }
      // Atmosphere fades in
      if (atmosphereRef.current) {
        const opacity = Math.max(0, (time - 2) / 2); // starts fading in halfway
        (atmosphereRef.current.material as THREE.MeshStandardMaterial).opacity = opacity;
        const scale = 1.2 + (time / 4) * 0.5; // grows larger than core
        atmosphereRef.current.scale.set(scale, scale, scale);
      }
    } else if (time >= 4 && time < 5) {
      if (phase !== 'formed') setPhase('formed');
    } else {
      // Reset
      if (particlesRef.current) {
        const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
        for(let i=0; i<initialData.pos.length; i++) {
          positions[i] = initialData.pos[i];
        }
        particlesRef.current.geometry.attributes.position.needsUpdate = true;
      }
      if (atmosphereRef.current) {
         (atmosphereRef.current.material as THREE.MeshStandardMaterial).opacity = 0;
      }
    }
  });

  return (
    <group>
      {/* Rocky Core */}
      <mesh ref={coreRef}>
        <icosahedronGeometry args={[1, 1]} /> {/* Low poly */}
        <meshStandardMaterial color="#8B7355" roughness={0.9} flatShading />
      </mesh>
      
      {/* Gas Atmosphere */}
      <mesh ref={atmosphereRef}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial color="#A5B4FC" transparent opacity={0} roughness={0.3} />
      </mesh>
      
      {/* Particles */}
      <points ref={particlesRef} geometry={particlesGeo}>
        <pointsMaterial size={0.15} vertexColors={true} transparent opacity={0.6} />
      </points>

      {/* Labels */}
      {phase === 'sweeping' && (
        <>
          <Html position={[0, -2, 0]} center>
            <div style={{ color: '#8B7355', fontWeight: 'bold', textShadow: '0 0 4px black', whiteSpace: 'nowrap' }}>Rocky/icy core</div>
          </Html>
          <Html position={[0, 4, 0]} center>
            <div style={{ color: '#3B82F6', fontWeight: 'bold', textShadow: '0 0 4px black', whiteSpace: 'nowrap' }}>Hydrogen + Helium gas</div>
          </Html>
        </>
      )}
      {phase === 'formed' && (
        <Html position={[0, 2, 0]} center>
          <div style={{ color: '#A5B4FC', fontWeight: 'bold', fontSize: '18px', textShadow: '0 0 4px black', whiteSpace: 'nowrap' }}>Gas giant forms!</div>
        </Html>
      )}
    </group>
  );
};

export const GasGiantSweep: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height), borderRadius: '8px', overflow: 'hidden' }}>
        <Canvas camera={{ position: [0, 2, 15], fov: 45 }}>
          <ambientLight intensity={0.3} />
          <directionalLight position={[10, 10, 5]} intensity={1} />
          <CoreAndGas />
          <OrbitControls enableZoom={true} enablePan={false} />
        </Canvas>
      </div>
    </div>
  );
};
